import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, ScrollText, Globe } from 'lucide-react';

const LEGAL = {
  terms: {
    icon: ScrollText,
    title: 'Terms of Service',
    updated: 'Last updated: July 2026',
    intro:
      'These Terms of Service ("Terms") govern your access to and use of the Udvega employee engagement and burnout analytics platform (the "Service"). By creating an account or using the Service, you agree to these Terms.',
    sections: [
      {
        h: '1. The Service',
        body: [
          'Udvega provides an AI-powered platform that collects anonymous employee feedback via surveys and produces aggregated, team-level analytics on engagement, stress, and burnout risk. The Service is intended for use by employers and their authorized HR representatives ("you").',
          'Udvega is purpose-built for workplace culture and predictive retention analytics. It is not a system of record for payroll, attendance, benefits, or other transactional HR functions.',
        ],
      },
      {
        h: '2. Accounts & Eligibility',
        body: [
          'To use the Service you must register with a valid work email and verify it. You are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account.',
          'You confirm that you are authorized by your organization to upload employee contact details for the sole purpose of distributing anonymous surveys.',
        ],
      },
      {
        h: '3. Acceptable Use',
        body: [
          'You agree not to misuse the Service, including attempting to de-anonymize responses, reverse-engineer the platform or its models, circumvent the anonymity safeguards, or use the Service to harass, surveil, or retaliate against any individual.',
          'Analytics are provided for organizational improvement. You agree to use insights responsibly and in compliance with applicable employment and privacy laws.',
        ],
      },
      {
        h: '4. Anonymity & Data',
        body: [
          'Survey responses are anonymized by design. Once a response is submitted, it is permanently unlinked from the invitation token. Neither you nor Udvega can trace an individual response back to a specific employee.',
          'Your use of employee data is further described in our Privacy Policy and GDPR Compliance statement, which are incorporated into these Terms.',
        ],
      },
      {
        h: '5. Intellectual Property',
        body: [
          'The Service, including its software, machine-learning models, and design, is owned by Udvega and protected by intellectual property laws. These Terms do not grant you any ownership rights in the Service.',
        ],
      },
      {
        h: '6. Disclaimers & Limitation of Liability',
        body: [
          'The Service provides predictive insights and is offered on an "as is" basis. Predictions are probabilistic and should support, not replace, human judgment. To the maximum extent permitted by law, Udvega is not liable for indirect or consequential damages arising from your use of the Service.',
        ],
      },
      {
        h: '7. Termination',
        body: [
          'You may stop using the Service at any time. We may suspend or terminate access for violations of these Terms. Upon termination, aggregated anonymous analytics may be retained, while account and roster data are handled per our Privacy Policy.',
        ],
      },
      {
        h: '8. Changes & Contact',
        body: [
          'We may update these Terms from time to time; material changes will be communicated within the platform. Questions about these Terms can be sent to legal@udvega.com.',
        ],
      },
    ],
  },
  privacy: {
    icon: ShieldCheck,
    title: 'Privacy Policy',
    updated: 'Last updated: July 2026',
    intro:
      'This Privacy Policy explains how Udvega collects, uses, and protects information when you use our platform. Protecting the psychological safety of employees is a core design principle, not an afterthought.',
    sections: [
      {
        h: '1. Information We Collect',
        body: [
          'Account information: the name, work email, password (stored only as a secure hash), and optional company name of HR users who register.',
          'Employee roster: the names, work emails, and department details that an HR user uploads solely to distribute anonymous survey invitations.',
          'Survey responses: anonymous answers submitted by employees. These are never linked to a named individual once submitted.',
        ],
      },
      {
        h: '2. How We Use Information',
        body: [
          'We use account data to authenticate you and operate the Service. We use roster data only to send survey invitations and reminders. We process anonymous responses to generate aggregated, team-level analytics and burnout-risk predictions.',
          'We do not sell personal data, and we do not use employee responses for advertising.',
        ],
      },
      {
        h: '3. Anonymity Architecture',
        body: [
          'Surveys are distributed using single-use tokens. The instant a response is submitted, the platform severs the link between the token and the stored answers, making it cryptographically impractical to attribute a response to any person.',
          'An "anonymity firewall" suppresses analytics for any department or segment with fewer than two respondents, preventing identity inference from small groups.',
        ],
      },
      {
        h: '4. Data Retention',
        body: [
          'Account and roster data are retained while your account is active. Anonymous survey answers are retained in aggregate to support historical trend analysis. You can request deletion of your account and associated roster data at any time.',
        ],
      },
      {
        h: '5. Security',
        body: [
          'We apply industry-standard safeguards including hashed passwords, HttpOnly session cookies, token-based survey access, and transport encryption. Access to data is limited to what is necessary to operate the Service.',
        ],
      },
      {
        h: '6. Your Rights & Cookies',
        body: [
          'Depending on your jurisdiction, you may have rights to access, correct, or delete personal data we hold about you. We use strictly necessary cookies to keep you signed in; we do not use them to track you across other websites.',
          'To exercise any right or ask a question, contact privacy@udvega.com.',
        ],
      },
    ],
  },
  gdpr: {
    icon: Globe,
    title: 'GDPR Compliance',
    updated: 'Last updated: July 2026',
    intro:
      'Udvega is built with privacy-by-design principles that align with the EU General Data Protection Regulation (GDPR) and similar frameworks. This statement summarizes how we support compliance.',
    sections: [
      {
        h: '1. Roles: Controller and Processor',
        body: [
          'When your organization uploads its employee roster, your organization acts as the data controller and Udvega acts as the data processor, handling data only on your documented instructions.',
        ],
      },
      {
        h: '2. Lawful Basis & Data Minimization',
        body: [
          'Processing is grounded in the legitimate interests of improving workplace wellbeing and, where applicable, your compliance obligations as an employer. We collect only what is required: minimal roster contact details for distribution, and anonymous responses for analysis.',
        ],
      },
      {
        h: '3. Anonymization',
        body: [
          'Survey responses are anonymized at the point of submission by unlinking them from their invitation token. Because individual responses cannot be attributed to a person, the resulting analytics fall outside the scope of personal data.',
          'The two-respondent minimum threshold provides an additional guarantee against re-identification.',
        ],
      },
      {
        h: '4. Data Subject Rights',
        body: [
          'We support the rights of access, rectification, erasure, restriction, portability, and objection. Because responses are anonymized, requests relating to submitted survey answers are addressed at the roster and account level. Contact your HR administrator or dpo@udvega.com.',
        ],
      },
      {
        h: '5. International Transfers & Sub-processors',
        body: [
          'Where data is transferred internationally, we rely on appropriate safeguards such as Standard Contractual Clauses. We maintain a list of sub-processors and require them to uphold equivalent protection.',
        ],
      },
      {
        h: '6. Data Processing Agreement & Breach Notification',
        body: [
          'A Data Processing Agreement (DPA) is available to customers on request. In the event of a personal data breach, we will notify affected controllers without undue delay and cooperate on any required regulatory reporting.',
        ],
      },
    ],
  },
};

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };

export default function Legal({ type }) {
  const doc = LEGAL[type];
  if (!doc) return null;
  const Icon = doc.icon;

  return (
    <div className="pt-28 pb-16 px-6 md:px-8 max-w-3xl mx-auto" data-testid={`legal-${type}`}>
      <motion.div initial="hidden" animate="visible" variants={fadeUp}>
        <div className="w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center mb-5">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-800">{doc.title}</h1>
        <p className="text-xs text-slate-400 mt-2">{doc.updated}</p>
        <p className="text-base text-slate-600 leading-relaxed mt-5">{doc.intro}</p>

        <div className="mt-10 space-y-8">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-heading text-lg font-medium text-slate-800 mb-3">{s.h}</h2>
              <div className="space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-slate-400 mt-12 border-t border-slate-200 pt-6">
          This document is a general template describing how Udvega operates and is provided for informational purposes.
          It does not constitute legal advice; please have it reviewed by qualified counsel before relying on it.
        </p>
      </motion.div>
    </div>
  );
}
